import React, { useEffect } from "react";
import { useCreateBlockNote } from "@blocknote/react";
import { BlockNoteView } from "@blocknote/mantine";
import { Plugin, PluginKey, Selection, TextSelection } from "@tiptap/pm/state";
import { GapCursor } from "@tiptap/pm/gapcursor";
import {
  filterInvalidTables,
  filterEmptyHeadings,
  removeAllTables,
} from "./blockNoteUtils";
// Provides .rw-readonly-blocknote rules (image click-block, defense-in-depth
// alongside the node-selection guard below). Imported here so the styling
// travels wherever this component renders, including the employee reader.
import "./styles.css";

// Fresh object each call — never share a block array/object across editors,
// BlockNote may mutate (normalize/assign ids) the blocks it's handed.
const emptyDoc = () => [{ type: "paragraph", content: [] }];

// Suppresses ProseMirror node selections (the blue ring) in the read-only
// renderer. Keyed so it can be unregistered on unmount.
const NO_NODE_SELECTION_KEY = new PluginKey("rwReadonlyNoNodeSelection");

/**
 * Read-only preview of one section's BlockNote document.
 *
 * The editor is created EMPTY and content is loaded in an effect — but the
 * editor is created fresh PER SECTION: callers render this with
 * `key={sectionUUID}` so switching sections mounts a clean editor. That is what
 * fixes the original bug. There is no shared, long-lived editor whose
 * `editor.document` desyncs across repeated remove/replace cycles, which threw
 * "Block with ID … not found" (or its catch re-threw an unguarded insertBlocks),
 * blanking the pane or white-screening the app.
 *
 * Loading via an effect (rather than `initialContent`) preserves the
 * table-stripping fallback: a table can pass shape validation
 * (`filterInvalidTables`) yet still make BlockNote throw. We retry with all
 * tables removed instead of crashing — and because seeding happens in an effect,
 * a throw is caught rather than escaping the render and white-screening.
 *
 * `schema` is built by the caller (the custom block specs differ between the
 * manager editors and the employee reader) and must be stable for the editor's
 * lifetime — memoize it in the parent.
 */
export default function SectionPreview({ schema, asJson }) {
  const editor = useCreateBlockNote({ schema });

  // Read-only: stop ProseMirror from ever applying a node selection. Clicking
  // media (image / video) or a content="none" embed (swipe deck / PDF /
  // signature) otherwise draws a blue selection ring AND forces a double-tap on
  // outside buttons like "Complete" — the first tap only clears the selection.
  // Filtering out the selection transaction prevents the ring on every block
  // type, while the blocks' own DOM interactions (video playback, opening a
  // deck/PDF, form inputs) are untouched since those aren't editor transactions.
  useEffect(() => {
    const tt = editor?._tiptapEditor;
    if (!tt || typeof tt.registerPlugin !== "function") return undefined;
    const plugin = new Plugin({
      key: NO_NODE_SELECTION_KEY,
      // NodeSelection has a `.node`; TextSelection / AllSelection do not. Reject
      // only PURE selection transactions that select a whole block node — gating
      // on !docChanged so a content load (replaceBlocks) that happens to set a
      // selection is never dropped along with its doc change.
      filterTransaction: (tr) =>
        !(
          tr.selectionSet &&
          !tr.docChanged &&
          tr.selection &&
          tr.selection.node
        ),
      // A doc-changing transaction can't be rejected (it carries the content),
      // but replaceBlocks on load can still LEAVE a node selection — e.g. on the
      // last block when it's an atom (image / swipe deck) — showing a blue ring
      // that filterTransaction then makes un-clearable by clicking another
      // block. Fix it up: move the cursor to the FIRST text block so nothing is
      // ring-selected. (Searching near sel.from just re-picks the same trailing
      // leaf as a node selection, which is why the ring persisted.)
      appendTransaction: (transactions, oldState, newState) => {
        const sel = newState.selection;
        if (!sel || !sel.node) return null;
        let textPos = null;
        newState.doc.descendants((node, pos) => {
          if (textPos !== null) return false;
          if (node.isTextblock) {
            textPos = pos + 1; // inside the first text block
            return false;
          }
          return true;
        });

        let cursor;
        if (textPos !== null) {
          cursor = TextSelection.create(newState.doc, textPos);
        } else {
          // No text block at all — a media-only section (PDF / swipe deck /
          // signature are content:"none" atoms). Selection.atStart would fall
          // back to a NodeSelection (ring) here, so use a gap cursor instead: a
          // non-node selection that draws nothing in this read-only view. Scan
          // for the first valid gap position; only if none exists fall back.
          let gap = null;
          for (let p = 0; p <= newState.doc.content.size; p++) {
            const $p = newState.doc.resolve(p);
            if (GapCursor.valid($p)) {
              gap = new GapCursor($p);
              break;
            }
          }
          cursor = gap || Selection.atStart(newState.doc);
        }
        return newState.tr.setSelection(cursor);
      },
    });
    tt.registerPlugin(plugin);
    return () => {
      try {
        tt.unregisterPlugin && tt.unregisterPlugin(NO_NODE_SELECTION_KEY);
      } catch (_) {
        /* editor already torn down */
      }
    };
  }, [editor]);

  useEffect(() => {
    if (!editor) return;
    // Clone before handing blocks to the editor. BlockNote mutates (normalizes /
    // reassigns ids on) the blocks it's given, and `asJson` is often the LIVE
    // `editor.document` of the section drill-in editor — the inputs modal writes
    // its editor.document straight into the shared `modalDataSection` on every
    // edit. Sharing those objects lets this preview corrupt that editor, and the
    // next re-seed then throws and blanks this pane to white (the bug seen when
    // closing the SectionInputsModal). A deep copy keeps this editor isolated.
    const cloned = Array.isArray(asJson)
      ? JSON.parse(JSON.stringify(asJson))
      : [];
    const filtered = filterEmptyHeadings(filterInvalidTables(cloned));
    try {
      editor.replaceBlocks(
        editor.document,
        filtered.length > 0 ? filtered : emptyDoc()
      );
    } catch (err) {
      console.error("SectionPreview: load failed, retrying without tables", err);
      // Last resort: strip ALL tables (including nested) and retry. Mirrors the
      // old loadAllBlocks fallback. Wrapped so a second failure can't escape the
      // effect and white-screen the app.
      try {
        const withoutTables = removeAllTables(cloned);
        editor.replaceBlocks(
          editor.document,
          withoutTables.length > 0 ? withoutTables : emptyDoc()
        );
      } catch (fallbackErr) {
        console.error(
          "SectionPreview: load failed after stripping tables",
          fallbackErr
        );
      }
    }
    // editor is stable for this component's lifetime (keyed remount per
    // section); reload whenever the section's blocks change in place.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asJson]);

  return (
    <div className="rw-readonly-blocknote">
      <BlockNoteView editor={editor} editable={false} theme="light" />
    </div>
  );
}
